/**
 * Delivery date — evergreen model.
 *
 * window.StockedDelivery: shared logic for the cart-level delivery date.
 *   - Dates are generated by rule and anchored to whole calendar weeks
 *     (selectableDates() / selectableWeeks(): Sun/Mon/Tue × lookahead weeks), computed
 *     in store time. No per-date metaobjects required.
 *   - One date per order, stored in localStorage and mirrored to cart attributes
 *     (delivery_date / _label / _timestamp / _cutoff). The cart attribute drives the
 *     server-rendered checkout gate and carries the date onto the order for fulfilment.
 *   - No default selection. On shop surfaces (body[data-shop-surface], set per-template
 *     in layout/theme.liquid) enforceGate() blocks the page behind #StockedDeliveryGate
 *     until a date is chosen (see openGate() below) -- the catalog is a function of the
 *     delivery week, so browsing without a date doesn't make sense. If an add-to-cart
 *     would violate a Special's earlier delivery window, product-form.js calls
 *     requireSelection(), which reuses the same dialog non-blocking (dismissible) to ask
 *     for an earlier date; the inline cart calendar lets the customer change it later.
 *
 * <cart-delivery-date>: the month-grid calendar UI, built on the module.
 *
 * Vanilla — no external dependencies.
 */

(function () {
  const STORAGE_KEY = 'stockedDeliveryDate';
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  /* ---------------------------------------------------- schedule rule config */

  // The store's timezone — ALL "today / cutoff" math is done in this zone so the
  // customer's browser timezone never changes which dates are offered.
  const DELIVERY_TZ = 'America/New_York';

  // Delivery rules (weekdays, blackouts, lookahead, per-date cutoff days-before + overrides)
  // come from the shared stocked.delivery_config metafield, injected by Liquid as
  // window.stockedDeliveryConfig. The validation function reads the same metafield, so the
  // calendar and the checkout gate can't disagree. The defaults below are guardrails for a
  // missing/malformed field. (The cutoff TIME of 12:30 is intentionally NOT here — it's
  // hardcoded in deliveryCutoff() and the function's input-query literal; change one, change both.)
  function config() {
    var c = window.stockedDeliveryConfig;
    if (typeof c === 'string') {
      try { c = JSON.parse(c); } catch (e) { c = null; }
    }
    return c && typeof c === 'object' ? c : {};
  }

  // Stocked delivers on these weekdays (0 = Sun, 1 = Mon, 2 = Tue) — from config.
  function deliveryWeekdays() {
    var w = config().delivery_weekdays;
    return Array.isArray(w) && w.length ? w : [0, 1, 2];
  }

  // Per-date cutoff inputs from config: `daysBefore` (days before EACH delivery date; the
  // 12:30 time is hardcoded in deliveryCutoff()) and `overrides` (deliveryISO -> cutoff date).
  function cutoffConfig() {
    var c = config();
    var n = c.cutoff_days_before_delivery;
    var o = c.cutoff_overrides;
    return {
      daysBefore: Number.isInteger(n) ? n : 4,
      overrides: (o && typeof o === 'object' && !Array.isArray(o)) ? o : {},
    };
  }

  /* ----------------------------------------------------------- date helpers */

  function lookaheadWeeks() {
    return parseInt(config().lookahead_weeks, 10) || 3;
  }

  // Whether a week with no open dates consumes a horizon slot. Default true (skip it),
  // so the customer always gets N weeks they can actually order in.
  function skipClosedWeeks() {
    var v = config().lookahead_skips_closed_weeks;
    return v == null ? true : !!v;
  }

  // Current wall-clock in the store timezone, as plain numbers (no browser-TZ drift).
  function nowET() {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: DELIVERY_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(new Date())
      .forEach((p) => {
        if (p.type !== 'literal') parts[p.type] = p.value;
      });
    let hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0; // some engines emit '24' at midnight
    return { year: +parts.year, month: +parts.month, day: +parts.day, hour, minute: +parts.minute };
  }

  // Weekday of a calendar date (0 = Sun). UTC construction → no DST/TZ ambiguity.
  function weekdayOf(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }

  // Shift a calendar date by n days using UTC math (DST-safe), returns {y,m,d}.
  function addDays(y, m, d, n) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + n);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }

  function ymdToInt(y, m, d) {
    return y * 10000 + m * 100 + d;
  }

  function isoOf(y, m, d) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  // A local Date built from calendar components — getDay()/getMonth()/toLocaleDateString
  // all reflect those exact components regardless of browser timezone.
  function localDate(y, m, d) {
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  function formatLong(day) {
    return day.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // "Monday, Sep 21st" — used where the full formatLong() (with year) reads as too
  // much text, e.g. the cart's inline delivery-date summary.
  function ordinalSuffix(n) {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  function formatShort(day) {
    const weekday = day.toLocaleDateString('en-US', { weekday: 'long' });
    const month = day.toLocaleDateString('en-US', { month: 'short' });
    const date = day.getDate();
    return `${weekday}, ${month} ${date}${ordinalSuffix(date)}`;
  }

  function toISO(year, month, day) {
    return isoOf(year, month + 1, day); // month here is 0-based (calendar grid)
  }

  // Holiday / non-delivery dates (ISO strings) from the delivery_blackout metaobject.
  function blackoutSet() {
    const list = Array.isArray(config().blackouts) ? config().blackouts : [];
    return new Set(list.map((v) => String(v).slice(0, 10)));
  }

  function cutoffPassed(y, m, d, now) {
    const c = window.StockedDeliveryFormat.deliveryCutoff(y, m, d, cutoffConfig());
    // Compare down to the minute (cutoff is 12:30, not on an hour boundary).
    const cutoffInt = ymdToInt(c.y, c.m, c.d) * 10000 + c.hour * 100 + c.minute;
    const nowInt = ymdToInt(now.year, now.month, now.day) * 10000 + now.hour * 100 + now.minute;
    return nowInt >= cutoffInt;
  }

  /* -------------------------------------------------------- date generation */

  // Every open delivery date within a generous scan, before the week horizon is applied.
  function openDates() {
    const now = nowET();
    const weekdays = deliveryWeekdays();
    const blackouts = blackoutSet();
    const out = [];
    for (let i = 0; i <= 366; i += 1) {
      const { y, m, d } = addDays(now.year, now.month, now.day, i);
      if (!weekdays.includes(weekdayOf(y, m, d))) continue;
      const iso = isoOf(y, m, d);
      if (blackouts.has(iso)) continue;
      if (cutoffPassed(y, m, d, now)) continue;
      out.push({ iso, y, m, d, day: localDate(y, m, d) });
    }
    return out;
  }

  // The ordered week codes the customer may choose from.
  function selectableWeeks() {
    const fmt = window.StockedDeliveryFormat;
    return fmt.horizonWeeks(openDates().map((e) => e.iso), lookaheadWeeks(), skipClosedWeeks());
  }

  // Open dates restricted to the week horizon. WEEK-anchored, not count-based: with a
  // count, N*weekdays dates can spill into an N+1th calendar week, which would let a
  // product bounded to week N be bought via a stray date in week N+1.
  function selectableDates() {
    const fmt = window.StockedDeliveryFormat;
    const weeks = selectableWeeks();
    return openDates().filter((e) => weeks.indexOf(fmt.weekCodeOf(e.iso)) !== -1);
  }

  /* --------------------------------------------------------- special dates */

  // Configured special (catering) dates that can still be ordered: past their
  // `opens` date, not blacked out, and before their own cutoff. Same entry shape as
  // openDates() plus `special: true`. Never part of openDates()/selectableDates():
  // a special date neither consumes nor extends the regular week horizon, and the
  // hero picker, gate and empty-cart calendar must never offer one.
  function openSpecialDates() {
    const fmt = window.StockedDeliveryFormat;
    const cfg = config();
    const now = nowET();
    const today = isoOf(now.year, now.month, now.day);
    const blackouts = blackoutSet();
    return Object.keys(fmt.specialDatesOf(cfg))
      .sort()
      .filter((iso) => fmt.specialDateOpen(iso, cfg, today) && !blackouts.has(iso))
      .map((iso) => {
        const y = +iso.slice(0, 4);
        const m = +iso.slice(5, 7);
        const d = +iso.slice(8, 10);
        return { iso, y, m, d, day: localDate(y, m, d), special: true };
      })
      .filter((e) => !cutoffPassed(e.y, e.m, e.d, now));
  }

  // Store-time today as an ISO date: what a special date's `opens` is compared against.
  function todayISO() {
    const now = nowET();
    return isoOf(now.year, now.month, now.day);
  }

  // Every date the cart may legitimately hold: the regular horizon plus open special
  // dates. This -- not selectableDates() -- is what findDate() and the frozen-cart
  // check search, or a healthy catering cart would read as 'stale' and be emptied.
  function knownDates() {
    return selectableDates()
      .concat(openSpecialDates())
      .sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));
  }

  // The slots (week codes + special-date ISOs) a cart line or product can be
  // delivered in right now -- the special-date analogue of selectableWeeks().
  function selectableSlots() {
    return selectableWeeks().concat(openSpecialDates().map((e) => e.iso));
  }

  function slotOf(iso) {
    return window.StockedDeliveryFormat.slotOf(iso, config());
  }

  // The week a regular menu surface renders for the current selection (see
  // StockedDeliveryFormat.menuSlot): a special-date selection shows the menu as the
  // first regular date's week. assets/availability-filter.js reads this.
  function menuSlot() {
    var sel = getSelection();
    var first = selectableDates()[0];
    return window.StockedDeliveryFormat.menuSlot(sel ? sel.iso : null, config(), first ? first.iso : null);
  }

  function specialDateLabel(iso) {
    return window.StockedDeliveryFormat.specialDateLabel(iso, config());
  }

  // Names a slot for copy: a week's window ("Nov 22nd - 24th"), or a special date as
  // "Thanksgiving · Wed Nov 25".
  function slotLabel(slot) {
    if (window.StockedDeliveryFormat.isSpecialDate(slot, config())) {
      return window.StockedDeliveryFormat.specialDateTitle(slot, config()) || '';
    }
    return windowLabelForWeek(slot);
  }

  function findDate(iso) {
    return knownDates().find((e) => e.iso === iso) || null;
  }

  /* ------------------------------------------------------ allowed-weeks intersection */

  // The weeks every non-exempt cart line can be delivered in. Empty array = unsatisfiable;
  // null = unconstrained. The two must stay distinguishable -- collapsing them would let
  // an impossible cart look unconstrained. `el` is the <cart-delivery-date> carrying the
  // raw per-line JSON emitted by snippets/delivery-date-selector.liquid
  // (data-cart-availability). Liquid never resolves the list-vs-bounds precedence itself
  // (that rule lives only in parseAvailability) -- it just ships the raw fields.
  function cartAllowedWeeks(el) {
    var raw = el && el.dataset && el.dataset.cartAvailability;
    if (!raw) return null;
    var lines;
    try { lines = JSON.parse(raw); } catch (e) { return null; }
    if (!Array.isArray(lines) || !lines.length) return null;
    var fmt = window.StockedDeliveryFormat;
    var horizon = selectableSlots();
    return fmt.intersectWeeks(lines.map(function (l) {
      return fmt.availableWeeks(
        fmt.parseAvailability({ startWeek: l.start, endWeek: l.end, weeks: l.weeks, dates: l.dates }),
        horizon
      );
    }));
  }

  // The unconstrained-vs-unsatisfiable predicate itself lives in
  // assets/delivery-allowed-weeks.js (pure, DOM-free, unit-tested under node:test) --
  // aliased locally so every call site below reads exactly as it did before the
  // extraction. Bound to the live config so a special date resolves to its own slot.
  var isWeekAllowed = function (iso, allowed) {
    return window.StockedAllowedWeeks.isWeekAllowed(iso, allowed, config());
  };

  // Mirrors the computed allowed-weeks set back onto the element as data-allowed-weeks
  // so other JS (product-form.js's own cart/product intersection) can read it without
  // recomputing. null -> attribute ABSENT (unconstrained); an array (including []) ->
  // attribute PRESENT with that JSON list. The two must render distinctly in the
  // markup: an absent attribute reads as "no constraint", so an unsatisfiable cart
  // ([]) has to be an explicit, present empty list -- never indistinguishable from no
  // attribute at all.
  function reflectAllowedWeeks(el, weeks) {
    if (!el) return;
    if (weeks === null) el.removeAttribute('data-allowed-weeks');
    else el.setAttribute('data-allowed-weeks', JSON.stringify(weeks));
  }

  function toSelection(entry) {
    const c = window.StockedDeliveryFormat.deliveryCutoff(entry.y, entry.m, entry.d, cutoffConfig());
    return {
      iso: entry.iso,
      // Generated dates have no metaobject id; use the ISO date as a stable handle/id.
      id: entry.iso,
      handle: entry.iso,
      label: formatLong(entry.day),
      timestamp: Math.floor(entry.day.getTime() / 1000),
      cutoffTimestamp: Math.floor(localDate(c.y, c.m, c.d).setHours(c.hour, c.minute) / 1000),
    };
  }

  /* ----------------------------------------------------------- localStorage */

  function getStored() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setStored(sel) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
    } catch (e) {
      /* ignore */
    }
  }

  function clearStored() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  // Current valid selection, or null if none / stale.
  function getSelection() {
    const s = getStored();
    return s && findDate(s.iso) ? s : null;
  }

  function select(iso) {
    const entry = findDate(iso);
    if (!entry) return null;
    const sel = toSelection(entry);
    setStored(sel);
    return sel;
  }

  /* --------------------------------------------------- cart writes (attrs) */

  function toAttributes(sel) {
    var weekCode = '';
    var windowLabel = '';
    var dueLabel = '';
    var fmt = window.StockedDeliveryFormat;
    if (sel && sel.iso && fmt) {
      var p = sel.iso.split('-');
      var y = parseInt(p[0], 10);
      var m = parseInt(p[1], 10);
      var d = parseInt(p[2], 10);
      weekCode = fmt.deliveryWeekCode(y, m, d);
      // A special date keeps its calendar week's code (spec 3.4) but names its own day.
      windowLabel = fmt.selectionWindowLabel(sel.iso, config(), deliveryWeekdays());
      dueLabel = fmt.deliveryDueLabel(y, m, d);
    }
    return {
      delivery_date: sel ? sel.iso : '',
      delivery_date_label: sel ? sel.label : '',
      delivery_date_timestamp: sel && sel.timestamp ? String(sel.timestamp) : '',
      delivery_date_cutoff: sel && sel.cutoffTimestamp ? String(sel.cutoffTimestamp) : '',
      delivery_week_code: weekCode,
      delivery_window_label: windowLabel,
      'Order Due Date': dueLabel,
    };
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  // Every cart write here passes through the delivery-date validation function
  // (stocked-extensions, extensions/delivery-date-validation), which answers 422
  // with a human-readable message when it refuses the cart. fetch() only rejects
  // on network failure, so WITHOUT this check a refusal reads as a success:
  // removeLines() counted a rejected line as removed and its callers reloaded
  // over an unchanged cart. That is exactly how a frozen cart reached support as
  // "the Remove and continue button does nothing" -- the 422 was there the whole
  // time, just never looked at. Rejects with the server's own copy so callers can
  // put it in front of the customer, and flags `cartRejected` so they can tell a
  // deliberate refusal apart from a network blip.
  function ajax(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.ok) return response;
      return response
        .json()
        .catch(function () { return null; })
        .then(function (data) {
          var message = (data && (data.description || data.message)) || 'Cart update failed (' + response.status + ')';
          var err = new Error(message);
          err.status = response.status;
          err.cartRejected = response.status === 422;
          throw err;
        });
    });
  }

  async function setCartAttributes(sel) {
    try {
      await ajax(cartUpdateUrl(), { attributes: toAttributes(sel) });
    } catch (e) {
      console.error('StockedDelivery: setCartAttributes failed', e);
    }
  }

  // True when the customer must still pick a date (and dates are available).
  function needsSelection() {
    return !getSelection() && selectableDates().length > 0;
  }

  // How requireSelection() will resolve for allowedWeeks, without side effects:
  //   'stored' an allowed selection is already stored (resolves to it),
  //   'auto'   exactly one offerable date and it is special (committed, no calendar),
  //   'modal'  the calendar modal opens,
  //   'none'   nothing offerable, or no modal to open (resolves null).
  function selectionPlan(allowedWeeks) {
    var sel = getSelection();
    if (sel && isWeekAllowed(sel.iso, allowedWeeks)) return { kind: 'stored', selection: sel };
    var candidates = window.StockedAllowedWeeks.offeredDates(knownDates(), allowedWeeks, config());
    if (!candidates.length) return { kind: 'none' };
    if (candidates.length === 1 && candidates[0].special) return { kind: 'auto', iso: candidates[0].iso };
    if (api._modal && typeof api._modal.open === 'function') return { kind: 'modal' };
    return { kind: 'none' };
  }

  // Whether requireSelection(opts) is about to show the calendar modal. product-form
  // feeds this to CartOpenPolicy (dateModalWasShown) -- needsSelection() can't stand in
  // for it, since a catering add auto-selects with nothing stored and no modal shown.
  function selectionPromptsModal(opts) {
    return selectionPlan((opts && opts.allowedWeeks) || null).kind === 'modal';
  }

  // Used by product-form on add: resolves to the selection, opening the modal
  // calendar first if none is chosen (or the stored one falls outside
  // opts.allowedWeeks, which holds slots: week codes and/or special-date ISOs).
  // Resolves null if there are no offerable dates within allowedWeeks, or the
  // customer dismisses the modal without choosing.
  function requireSelection(opts) {
    opts = opts || {};
    var allowedWeeks = opts.allowedWeeks || null;
    var plan = selectionPlan(allowedWeeks);
    if (plan.kind === 'stored') return Promise.resolve(plan.selection);
    if (plan.kind === 'none') return Promise.resolve(null);
    // A catering add whose only possible date is one special date: commit it without
    // the calendar (spec §6.1 step 4) -- there is nothing for the customer to choose.
    // Announced like every other commit so the bar, inline calendar and filter follow;
    // the caller (product-form.js) writes the cart attributes itself.
    if (plan.kind === 'auto') {
      var auto = select(plan.iso);
      if (auto) {
        document.dispatchEvent(new CustomEvent('delivery-date:changed', { detail: { date: auto.iso, label: auto.label } }));
      }
      return Promise.resolve(auto);
    }
    // plan.kind === 'modal'
    return api._modal.open({ allowedWeeks: allowedWeeks }).then(function (result) {
      // A dismissed constrained modal defaults to the stored selection; if that
      // stored date falls outside allowedWeeks, treat it as no-selection so the
      // add aborts instead of proceeding with a disallowed date.
      return (result && isWeekAllowed(result.iso, allowedWeeks)) ? result : null;
    });
  }

  // Raw per-line identity + availability for every non-exempt cart line (key,
  // title, start, end, weeks), read from the inline <cart-delivery-date>'s own
  // data-cart-availability (see snippets/delivery-date-selector.liquid). There is
  // only ever one non-modal <cart-delivery-date> on a page -- the modal instance
  // never carries this attribute (see cartAllowedWeeks() above) -- so this needs
  // no element argument, unlike cartAllowedWeeks(el).
  function cartLinesRaw() {
    var el = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
    var raw = el && el.dataset && el.dataset.cartAvailability;
    if (!raw) return [];
    try {
      var lines = JSON.parse(raw);
      return Array.isArray(lines) ? lines : [];
    } catch (e) {
      return [];
    }
  }

  // Drops one line (by key) from the inline <cart-delivery-date>'s
  // data-cart-availability and re-derives its allowed weeks, so cartLinesRaw() and
  // cartAllowedWeeks() describe the cart as it is after a removal the page hasn't
  // re-rendered yet. See removeLines().
  function forgetCartLine(key) {
    var el = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
    if (!el || !el.dataset || !el.dataset.cartAvailability) return;
    var remaining = cartLinesRaw().filter(function (line) { return !line || line.key !== key; });
    el.dataset.cartAvailability = JSON.stringify(remaining);
    if (typeof el.setAllowedWeeks === 'function') {
      el.setAllowedWeeks(cartAllowedWeeks(el));
      if (typeof el.refreshDates === 'function') el.refreshDates();
    }
  }

  function cartChangeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  // Evicts a set of cart lines identified by KEY, never by position. /cart/change.js
  // addresses a line by 1-based `line` index OR by `id` (variant id, or the line's
  // key when the same variant appears in more than one line). Indices SHIFT as
  // earlier lines are removed, so looping with `line` would silently remove the
  // wrong line after the first call in a multi-line batch; `id: line.key` is
  // stable regardless of how many other lines are removed alongside it, so each
  // call is independent and order-safe. Sequential (not Promise.all) -- concurrent
  // writes to the same cart can race server-side, one at a time is the safe
  // default. Lines missing a key are skipped rather than thrown on (defensive:
  // this only ever runs against StockedCartReconcile's own output).
  //
  // On full success, resolves normally. On a mid-chain failure (network blip,
  // etc.), REJECTS with an Error carrying `.removedCount` -- how many lines
  // actually came out server-side before the failure. This matters: a rejection
  // does NOT mean "nothing happened" -- earlier removals in the same batch are
  // already permanent on the server, and every caller needs that count to decide
  // whether a reload is required to keep the page honest about the cart's real
  // contents, even when the operation as a whole is being reported as failed.
  //
  // Each removed line is also dropped from the inline <cart-delivery-date>'s
  // data-cart-availability (forgetCartLine()). Nothing re-renders the drawer
  // between a removal and the add that may follow it, so without this
  // cartLinesRaw() kept listing the removed lines: a menu add over a catering cart
  // opens the calendar after the removal, and its [data-gate-continue] commit saw
  // the gone catering line as a conflict again, "removed" it a second time, and
  // reloaded over the add it was meant to finish.
  function removeLines(lines) {
    var list = Array.isArray(lines) ? lines : [];
    var removedCount = 0;
    return list
      .reduce(function (chain, line) {
        if (!line || !line.key) return chain;
        return chain.then(function () {
          return ajax(cartChangeUrl(), { id: line.key, quantity: 0 }).then(function (response) {
            removedCount += 1;
            forgetCartLine(line.key);
            return response;
          });
        });
      }, Promise.resolve())
      .catch(function (err) {
        var wrapped = err instanceof Error ? err : new Error(String(err));
        wrapped.removedCount = removedCount;
        throw wrapped;
      });
  }

  // Pretty date-range label for a week CODE (e.g. "Aug 24 - 26"), for callers that
  // need to name a week before any specific day within it has been chosen (the
  // add-to-cart entry point proposes the earliest week a product is deliverable
  // in). Built from the week's own first delivery day, so it stays correct
  // whichever weekdays are configured.
  function windowLabelForWeek(code) {
    var fmt = window.StockedDeliveryFormat;
    var weekdays = deliveryWeekdays();
    var bounds = fmt.weekBounds(code, weekdays);
    if (!bounds) return '';
    var p = bounds.firstISO.split('-');
    return fmt.deliveryWindowLabel(+p[0], +p[1], +p[2], weekdays);
  }

  // One dialog, every entry point where COMMITTING a delivery date would evict
  // cart lines that can't come that week: changing the date with conflicting
  // lines already in the cart, adding a product the current date excludes, and
  // revalidate() finding the stored date's week no longer covers everything in
  // the cart. Generalised from the old Specials-only promptSpecialConflict.
  // Always NAMES the casualties -- an empty cart after a menu swap must never be
  // a silent outcome. `conflicts` is an array of {key, title, ...}; only `title`
  // (and an optional `reason`) is rendered. Resolves 'confirm' or 'cancel'. If
  // the dialog markup is missing, resolves 'cancel' -- fail CLOSED here (unlike
  // the old promptSpecialConflict's 'change' default): a missing dialog must
  // never silently remove cart lines.
  //
  // Traps focus like every other modal-like surface in this theme (global.js
  // trapFocus/removeTrapFocus) -- this one is invoked WHILE the blocking gate or
  // constrained re-pick can already be open underneath it (see hasOpenDialog()),
  // and trapFocus() isn't stack-aware: it unconditionally replaces whatever trap
  // was previously active. So the underlying dialog's trap is remembered up front
  // and explicitly re-established on every exit path (confirm, cancel, overlay
  // click, Escape) rather than just released -- otherwise closing this dialog
  // would leave the STILL-OPEN gate/modal underneath with no trap at all.
  // The mechanics above, shared by the reconcile dialog and the frozen-cart
  // recovery dialog -- both open over the same gate/re-pick calendar and need the
  // identical trap handling, and duplicating that was how the two would drift.
  // Each caller fills its own content first, then hands the element here.
  // Resolves 'confirm' or 'cancel'.
  //
  // opts.hold keeps the dialog on screen after 'confirm' instead of closing it,
  // with its buttons disabled and the confirm label swapped for [data-busy-label].
  // Recovery needs that: its work ends in a page load, and closing the dialog
  // first left a stretch of untouched page where the customer had no idea
  // anything was happening. The caller closes it with dismissDialog() if the work
  // fails -- otherwise the navigation takes it away.
  var dialogClosers = new WeakMap();

  function dismissDialog(el) {
    var close = el && dialogClosers.get(el);
    if (close) close();
  }

  function setDialogBusy(el, busy) {
    var confirmBtn = el.querySelector('[data-dialog-confirm]');
    var cancelBtn = el.querySelector('[data-dialog-cancel]');
    if (cancelBtn) cancelBtn.disabled = busy;
    if (!confirmBtn) return;
    confirmBtn.disabled = busy;
    confirmBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    var busyLabel = confirmBtn.dataset.busyLabel;
    if (!busyLabel) return;
    if (busy) {
      confirmBtn.dataset.idleLabel = confirmBtn.textContent;
      confirmBtn.textContent = busyLabel;
    } else if (confirmBtn.dataset.idleLabel) {
      confirmBtn.textContent = confirmBtn.dataset.idleLabel;
    }
  }

  function runDialog(el, opts) {
    opts = opts || {};
    var dialogEl = el.querySelector('[data-dialog-box]');
    var focusTarget = dialogEl && dialogEl.querySelector('[data-dialog-cancel]');

    // Remember the underlying gate/modal's own trap (if one is active) so it can
    // be restored on close, rather than just released -- see the function comment.
    var gate = document.getElementById('StockedDeliveryGate');
    var gateDialog = gate && !gate.hidden ? gate.querySelector('.delivery-gate__dialog') : null;

    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-active'); });
    if (dialogEl && typeof trapFocus === 'function') {
      trapFocus(dialogEl, focusTarget || dialogEl);
    } else if (focusTarget) {
      focusTarget.focus();
    }

    return new Promise(function (resolve) {
      // Detaching the listeners is what makes a held dialog inert: the overlay and
      // Escape stop responding, so a customer can't dismiss the rebuild halfway
      // through and leave the cart half-written.
      function detach() {
        el.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKeydown);
      }
      function close() {
        dialogClosers.delete(el);
        setDialogBusy(el, false);
        el.classList.remove('is-active');
        el.hidden = true;
        detach();
        if (gateDialog && typeof trapFocus === 'function') {
          trapFocus(gateDialog); // restore the trap this dialog borrowed, not just release it
        } else if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }
      }
      function cleanup(result) {
        if (result === 'confirm' && opts.hold) {
          detach();
          setDialogBusy(el, true);
          dialogClosers.set(el, close);
          resolve(result);
          return;
        }
        close();
        resolve(result);
      }
      function onClick(e) {
        if (e.target.closest('[data-dialog-confirm]')) cleanup('confirm');
        else if (e.target.closest('[data-dialog-cancel], [data-dialog-overlay]')) cleanup('cancel');
      }
      function onKeydown(e) {
        if (e.key === 'Escape') cleanup('cancel');
      }
      el.addEventListener('click', onClick);
      document.addEventListener('keydown', onKeydown);
    });
  }

  // Renders `items` as the dialog's casualty list. Shared so both dialogs name
  // what they are about to remove in the same shape.
  function fillItemList(list, items) {
    if (!list) return;
    list.innerHTML = '';
    (items || []).forEach(function (c) {
      if (!c) return;
      var li = document.createElement('li');
      var title = c.title || c.product_title || '';
      li.textContent = c.reason ? title + ' (' + c.reason + ')' : title;
      list.appendChild(li);
    });
  }

  function promptReconcile(conflicts, windowLabel) {
    var el = document.getElementById('StockedReconcileDialog');
    if (!el) return Promise.resolve('cancel');
    var bodyTemplate = window.stockedReconcileBody || 'Moving to {{ window }} will remove:';
    var bodyEl = el.querySelector('[data-reconcile-body]');
    if (bodyEl) bodyEl.textContent = bodyTemplate.replace('{{ window }}', windowLabel || '');
    fillItemList(el.querySelector('[data-reconcile-items]'), conflicts);
    // Held open and busy through the removal AND the reload that follows it, for
    // the same reason as the recovery dialog: confirming here kicks off one cart
    // write per conflicting line and then a page load, and closing the dialog
    // first left the customer on a visibly unchanged page with nothing happening.
    // Every caller either ends in a reload or calls dismissReconcile() -- see
    // there for why a held dialog can't get stranded.
    return runDialog(el, { hold: true });
  }

  // Closes a HELD reconcile dialog. Needed on exactly the paths that confirm the
  // removal and then do NOT reload -- a held dialog is inert (its listeners are
  // detached so nobody can dismiss it mid-write), so anything that abandons the
  // operation has to take it down explicitly or the customer is stuck behind a
  // spinner forever. No-op when the dialog was already closed.
  function dismissReconcile() {
    dismissDialog(document.getElementById('StockedReconcileDialog'));
  }

  // Shared by every path that COMMITS a real (non-preview) date: the inline
  // cart-page calendar's direct pick, and the shared [data-gate-continue] handler
  // used by both the blocking gate and the constrained re-pick modal. Checks the
  // candidate date against every current cart line BEFORE anything is mutated --
  // if some lines can't come that week, the customer must explicitly confirm via
  // promptReconcile() (naming every casualty) before select()/setCartAttributes()
  // ever runs. Gating the mutation itself (rather than mutating then undoing on
  // cancel) is what keeps this consistent with the state-machine invariant this
  // component was audited against: every exit path leaves the source of truth
  // exactly as it was, with nothing to unwind. Resolves {proceed, removed}:
  // proceed is false on cancel OR a failed removal (fail open -- never apply a
  // date over a cart that's still inconsistent); removed is true only when lines
  // actually came out, so callers know a re-render (this file uses a full reload,
  // matching the existing CartRemoveButton convention -- see assets/cart.js) is
  // needed to reflect it.
  function reconcileForDate(iso) {
    // A stale-date clear still swapping the cart out (clearStaleCart()) must land
    // first: until it does, cartLinesRaw() still lists the old lines and the cookie
    // may still point at the frozen cart. If it failed, the cart is still frozen and
    // the recovery dialog is up -- committing a date now would only be refused.
    if (pendingClear) {
      return pendingClear.then(
        function () { return reconcileForDate(iso); },
        function () { return { proceed: false, removed: false }; }
      );
    }
    var reconcile = window.StockedCartReconcile;
    if (!reconcile) return Promise.resolve({ proceed: true, removed: false }); // fail open
    var slot = slotOf(iso);
    var conflicts;
    try {
      conflicts = reconcile.conflictingLines(cartLinesRaw(), slot ? [slot] : []);
    } catch (e) {
      console.error('StockedDelivery: conflictingLines() failed; proceeding without reconciliation', e);
      return Promise.resolve({ proceed: true, removed: false });
    }
    if (!conflicts.length) return Promise.resolve({ proceed: true, removed: false });
    return promptReconcile(conflicts, slotLabel(slot)).then(function (choice) {
      if (choice !== 'confirm') return { proceed: false, removed: false };
      return removeLines(conflicts)
        .then(function () { return { proceed: true, removed: true }; })
        .catch(function (err) {
          console.error('StockedDelivery: failed to remove conflicting lines', err);
          // Callers reload only when removed is true (below). When nothing came
          // out, nothing reloads, so the held dialog has to be taken down here.
          if (!(err && err.removedCount > 0)) dismissReconcile();
          // A rejection doesn't mean NOTHING was removed -- removeLines() reports
          // how many lines came out before the failure via err.removedCount. If
          // that's non-zero, the cart is already changed server-side regardless of
          // whether we go on to apply the new date, and `removed: true` here is
          // what makes the two commit-path callers below reload instead of leaving
          // the page showing a cart that no longer matches reality.
          return { proceed: false, removed: !!(err && err.removedCount > 0) };
        });
    });
  }

  // Shared by both commit paths below (selectDate() and [data-gate-continue]) for
  // the one narrow way they can otherwise diverge: the customer approves removing
  // conflicting lines via reconcileForDate() (result.removed === true), but by the
  // time select(iso) runs afterward the picked date itself has stopped being
  // selectable (its cutoff elapsed in the few seconds the dialog was open, a
  // blackout landed on it, etc.) and select() returns null. The removal already
  // happened server-side either way -- silence here would leave the customer
  // thinking their whole choice went through when only half of it did, after they
  // explicitly approved a destructive action. Reload regardless so the DOM
  // reflects the real (now-smaller) cart, and say so.
  // Every commit path ends the same way once lines were removed: reload so the DOM
  // matches the smaller cart. That reload MUST wait for the delivery-date attribute
  // write to land. The write is fired without being awaited (it is deliberately
  // background work -- select() has already persisted to localStorage, so the UI and
  // the line stamp on add are correct immediately), and location.reload() aborts
  // requests still in flight. Reloading straight after a commit therefore dropped
  // the new date on the floor while the removal -- which had already finished --
  // stuck, leaving exactly the state this whole flow exists to prevent: a cart whose
  // lines moved but whose delivery date did not.
  //
  // Swallows a rejection rather than skipping the reload: setCartAttributes() already
  // fails open internally, and a cart with removed lines must resync regardless of
  // whether the attribute write succeeded.
  function reloadAfter(pending) {
    Promise.resolve(pending).catch(function () {}).then(function () { location.reload(); });
  }

  function warnDateNotApplied() {
    console.error(
      'StockedDelivery: removed conflicting lines, but the picked date could no longer be applied'
    );
    try {
      window.alert(
        'Some items were removed, but your new delivery date could not be applied because it just closed. Please choose another date.'
      );
    } catch (e) {
      /* ignore -- alert() can throw in some sandboxed/embedded contexts */
    }
    location.reload();
  }

  /* ------------------------------------------------------- blocking date gate */

  // Generated from delivery_weekdays -- never hardcode the cadence copy, or it drifts
  // from the calendar directly above it (config lists 0-6, Sun-first, same as WEEKDAYS).
  function cadenceLabel() {
    return deliveryWeekdays()
      .slice()
      .sort(function (a, b) { return a - b; })
      .map(function (n) { return WEEKDAYS[n]; })
      .join(' · ');
  }

  function cadenceText() {
    return 'Delivering ' + cadenceLabel();
  }

  // Generic re-pick explanation for the constrained calendar: a week set (unlike the
  // old single max-date ceiling) has no one boundary to name, and WHICH item(s)
  // conflict is Task 13's job (promptReconcile names them individually) -- this is
  // only the dialog chrome shown while <cart-delivery-date> filters to allowedWeeks.
  function allowedWeeksReasonText() {
    return "An item in your cart isn't available on every date. Please choose a date that works for your whole cart.";
  }

  // `blocking` also drives which chrome shows: [data-gate-preshop] (the "before you
  // shop" eyebrow + generic body copy) only makes sense before the customer has
  // anything in their cart, so it's shown for the blocking gate and hidden for the
  // constrained re-pick -- which shows [data-gate-cancel] instead, a visible dismiss
  // affordance to match its (already-functional) overlay-click/Escape dismissal.
  function updateGateChrome(el, reason, blocking) {
    if (!el) return;
    var cadenceEl = el.querySelector('[data-gate-cadence]');
    if (cadenceEl) cadenceEl.textContent = cadenceText();
    var reasonEl = el.querySelector('[data-gate-reason]');
    if (reasonEl) {
      reasonEl.textContent = reason || '';
      reasonEl.hidden = !reason;
    }
    el.querySelectorAll('[data-gate-preshop]').forEach(function (node) {
      node.hidden = !blocking;
    });
    var cancelBtn = el.querySelector('[data-gate-cancel]');
    if (cancelBtn) cancelBtn.hidden = blocking;
  }

  // Reflects (or clears) the pending/committed pick in the gate footer. `entry` is a
  // selectableDates() entry ({iso, y, m, d, day}) or null/undefined for "nothing picked".
  function updateGateSelection(el, entry) {
    if (!el) return;
    var summaryEl = el.querySelector('[data-gate-summary]');
    var continueBtn = el.querySelector('[data-gate-continue]');
    if (summaryEl) summaryEl.textContent = entry ? 'Delivering on ' + formatLong(entry.day) : 'No date selected';
    if (continueBtn) continueBtn.disabled = !entry;
  }

  // Opens the gate as a BLOCKING dialog: no close button, no overlay-click dismissal,
  // no Escape handler (see <cart-delivery-date>.blocking below), and Tab is trapped
  // inside it via the theme's trapFocus/removeTrapFocus (assets/global.js -- every
  // other modal in this codebase uses it; a keyboard user must not be able to tab past
  // this one out to the page behind it). There is no default selection and no valid
  // dismissed state. Resolves with the chosen selection once the customer presses
  // Continue (wired at the bottom of this file).
  function openGate(reason) {
    var el = document.getElementById('StockedDeliveryGate');
    if (!el) return Promise.resolve(null);
    var dialog = el.querySelector('.delivery-gate__dialog');
    var modal = api._modal;
    if (modal) {
      modal.blocking = true;
      modal.pendingDate = null;
      modal.setAllowedWeeks(null);
      modal.refreshDates();
      modal.expanded = true;
      modal.hidden = false;
      modal.render();
    }
    updateGateChrome(el, reason, true);
    updateGateSelection(el, null);
    el.hidden = false;
    document.body.classList.add('delivery-gate-open');
    requestAnimationFrame(function () {
      el.classList.add('is-active');
      var focusTarget =
        (modal && modal.calendarEl && modal.calendarEl.querySelector('[data-delivery-day][tabindex="0"]')) ||
        (modal && modal.calendarEl && modal.calendarEl.querySelector('[data-delivery-day]')) ||
        dialog;
      if (dialog && typeof trapFocus === 'function') {
        trapFocus(dialog, focusTarget || dialog);
      } else if (focusTarget) {
        focusTarget.focus();
      }
    });
    return new Promise(function (resolve) {
      el.addEventListener('stocked:gate-resolved', function handler(e) {
        el.removeEventListener('stocked:gate-resolved', handler);
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        el.classList.remove('is-active');
        el.hidden = true;
        document.body.classList.remove('delivery-gate-open');
        resolve(e.detail && e.detail.selection);
      });
    });
  }

  // No default selection: the gate blocks shop surfaces until the customer chooses.
  // If nothing is orderable at all, don't trap the customer behind an unsatisfiable
  // dialog -- let the (already-empty) catalog render instead.
  function enforceGate() {
    // Task 15: a preview session (?delivery_week=...) has no stored date of its own --
    // it never writes one -- so without this the gate would block the very page the
    // merchant opened the preview link to check. Guarded defensively (existence +
    // typeof, not a bare call) so a script-order hiccup -- availability-filter.js
    // hasn't run yet, or failed to attach StockedAvailability -- can't throw here and
    // fall through to the enforceGate() catch below, which force-hides the gate
    // globally rather than just skipping it for this one case. previewWeek() itself is
    // already fail-safe (never throws; returns null on anything it can't resolve).
    if (window.StockedAvailability && typeof window.StockedAvailability.previewWeek === 'function'
      && window.StockedAvailability.previewWeek()) {
      return;
    }
    if (!document.body.hasAttribute('data-shop-surface')) return;
    if (getSelection()) return;
    if (!selectableDates().length) return;
    openGate(takeClearedNotice());
  }

  // A returning customer can hold a date that no longer exists (the horizon rolled
  // past it, a blackout landed on it, or the cutoff passed) and a cart of products
  // that are no longer deliverable. Clear localStorage AND the cart attributes
  // together -- clearing only storage leaves cart.attributes.delivery_date holding a
  // dead date, which the Liquid checkout gate (a presence check, see
  // snippets/cart-drawer.liquid / sections/main-cart-footer.liquid) reads as valid,
  // giving an enabled checkout button and a rejection from the validation function
  // moments later. Returns true when the stored selection (if any) is still valid, so
  // callers can skip redundant work (see the DOMContentLoaded wiring below, which
  // skips enforceGate()'s OWN openGate() call when this one already opened it).
  function revalidate() {
    // Task 15: under an active preview, revalidate() must not mutate anything at all --
    // including its own stale-date CLEANUP just below (clearStored()/setCartAttributes(null)
    // are real writes, not reads), which would otherwise run even though nothing about
    // preview should touch the customer's real stored state. Skips the ENTIRE body
    // (not just openGate()) rather than only suppressing the gate call: a stale real
    // date is left exactly as it was for the duration of the preview session, and gets
    // cleaned up normally the next time the page loads without the override. Returning
    // true (not false) is safe either way -- the DOMContentLoaded caller only uses the
    // return value to decide whether to ALSO call enforceGate(), and enforceGate()
    // carries the identical guard (see above) and no-ops under preview regardless.
    // Guarded the same defensive way as enforceGate()'s guard (existence + typeof, not
    // a bare call) so a script-order hiccup can't throw here either -- revalidate() runs
    // from the same try/catch'd DOMContentLoaded handler as enforceGate() (both scripts
    // have long finished executing by then), and from cart-drawer.js's open(), which
    // only fires on a later user interaction, well after every deferred script has run.
    if (window.StockedAvailability && typeof window.StockedAvailability.previewWeek === 'function'
      && window.StockedAvailability.previewWeek()) {
      return true;
    }
    // A frozen cart makes every branch below moot: none of them can write to it,
    // and the stale-date gate would ask the customer to pick a date whose write
    // would be refused. Offer the rebuild instead and stand down. Returning false
    // keeps enforceGate() from opening a second dialog on top, exactly as the
    // stale-date branch below does.
    if (offerRecovery()) return false;

    // A special-date selection left over from a catering cart that has since checked
    // out or been emptied: dropped here, before availability-filter.js's own
    // DOMContentLoaded apply() reads it, so the menu shows its normal "choose a date"
    // state instead of hiding every regular card. Kept on the catering surface and
    // whenever the cart still has lines.
    var leftover = getStored();
    if (leftover && window.StockedDeliveryFormat.shouldDropSpecialSelection({
      selectionISO: leftover.iso,
      cartLineCount: cartLinesRaw().length,
      specialSurface: document.body.hasAttribute('data-special-surface'),
      config: config(),
    })) {
      clearStored();
      // Through the delivery-date:changed bridge below, so the picker refreshes and
      // stocked:week-changed fires once (cart-drawer.js also calls revalidate() later,
      // after the filter's own page-load apply() has run).
      document.dispatchEvent(new CustomEvent('delivery-date:changed', { detail: { date: null, label: null } }));
    }

    var stored = getStored();
    if (stored && !findDate(stored.iso)) {
      clearStored();
      // Deliberately does NOT blank the cart's delivery attributes. Writing an
      // empty delivery_date leaves a cart that HAS lines and NO date, which the
      // validation function refuses (MSG_MISSING) -- and because it is evaluated
      // against the cart a write ARRIVES at, that refusal then applies to every
      // subsequent write too, including the one that would set a new date. On a
      // cart whose stored date has gone stale only in localStorage, the write
      // succeeds and freezes an otherwise healthy cart permanently. Leaving the
      // old attribute in place costs nothing (it was already stale server-side)
      // and the gate below replaces it with a valid one in a single write.
      renderBar();
      if (document.body.hasAttribute('data-shop-surface')) {
        openGate(window.stockedGateStaleMessage || '');
      }
      return false;
    }
    renderBar();

    // The date itself is still open, but its WEEK may no longer cover every cart
    // line -- cart composition can change underneath a long-idle tab, or a
    // product's own availability can change across a menu swap while the stored
    // date stays technically selectable (spec §7.5 step 3). Distinct from the
    // "date fell off the horizon" branch above, and routed through the same
    // reconcile dialog, but non-blockingly: the date IS valid, so revalidate()
    // itself still returns true here -- callers that care whether something is
    // currently ON SCREEN (see cart-drawer.js's open()) should check
    // hasOpenDialog() directly rather than overload this return value with a
    // second meaning.
    var sel = getSelection();
    if (sel) reconcileRevalidation(sel);
    return true;
  }

  // Whether a StockedDelivery-owned modal-like dialog (the blocking gate, the
  // constrained re-pick -- both #StockedDeliveryGate -- or the reconcile dialog)
  // is currently visible. Checked directly off DOM state, independent of WHY it's
  // open or which kind of page it's on, so it stays correct regardless (e.g. the
  // reconcile dialog can open from revalidate() on ANY page, not just
  // body[data-shop-surface] pages, unlike the blocking gate).
  function hasOpenDialog() {
    var gate = document.getElementById('StockedDeliveryGate');
    var reconcile = document.getElementById('StockedReconcileDialog');
    var recovery = document.getElementById('StockedRecoveryDialog');
    return !!(gate && !gate.hidden)
      || !!(reconcile && !reconcile.hidden)
      || !!(recovery && !recovery.hidden);
  }

  // Session-scoped "already asked about this" memory for the PASSIVE revalidation
  // path only (page load / cart-drawer open) -- not the active commit paths
  // (selectDate, [data-gate-continue]), where the customer just took a deliberate
  // action and re-prompting on every attempt is correct, not nagging. Without
  // this, a customer who clicks "Keep my current date" would see the identical
  // dialog again on the very next page navigation or drawer open, repeatedly,
  // until they either confirm or the conflict resolves itself some other way.
  //
  // The fingerprint itself (reconcileFingerprint()) lives in the pure, unit-tested
  // assets/cart-reconcile.js module alongside conflictingLines() -- it's the
  // target week code plus the sorted conflicting line keys, content-addressed
  // rather than a boolean flag, so it self-invalidates the moment the cart or the
  // date actually changes: a different set of conflicting lines, or a different
  // target week, produces a different fingerprint and is treated as a genuinely
  // new conflict regardless of what was dismissed before. Getting this wrong in
  // either direction (nagging forever, or silently suppressing a real new
  // conflict) is a correctness bug, which is why it's pulled out and tested
  // rather than left as untested glue here. The sessionStorage read/write/clear
  // around it stays here, since that part IS browser-storage-coupled glue.
  // Cleared outright once the conflict resolves (removed or no longer
  // conflicting), so nothing lingers in sessionStorage past its relevance. Never
  // persisted beyond the tab's session.
  var RECONCILE_DISMISSED_KEY = 'stockedReconcileDismissed';

  function readDismissedFingerprint() {
    try {
      return sessionStorage.getItem(RECONCILE_DISMISSED_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function rememberDismissedFingerprint(fingerprint) {
    try {
      sessionStorage.setItem(RECONCILE_DISMISSED_KEY, fingerprint);
    } catch (e) {
      /* ignore -- worst case, the dialog re-prompts next time */
    }
  }

  function clearDismissedFingerprint() {
    try {
      sessionStorage.removeItem(RECONCILE_DISMISSED_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function reconcileRevalidation(sel) {
    var reconcile = window.StockedCartReconcile;
    var fmt = window.StockedDeliveryFormat;
    if (!reconcile || !fmt) return; // fail open
    var slot = slotOf(sel.iso);
    var conflicts;
    try {
      conflicts = reconcile.conflictingLines(cartLinesRaw(), slot ? [slot] : []);
    } catch (e) {
      console.error('StockedDelivery: revalidate() conflict check failed', e);
      return;
    }
    if (!conflicts.length) {
      clearDismissedFingerprint(); // nothing to dismiss anymore -- don't let stale memory linger
      return;
    }
    // Fail open if the fingerprint helper itself is unavailable for any reason --
    // fall back to null, which never matches readDismissedFingerprint()'s stored
    // value, so the dialog just always prompts (the pre-dismissal-memory
    // behaviour) rather than throwing.
    var fingerprint =
      typeof reconcile.reconcileFingerprint === 'function'
        ? reconcile.reconcileFingerprint(conflicts, slot)
        : null;
    if (fingerprint !== null && fingerprint === readDismissedFingerprint()) return; // identical conflict, already dismissed this session
    promptReconcile(conflicts, slotLabel(slot)).then(function (choice) {
      if (choice !== 'confirm') {
        // Dismissed -- cart left as-is, checkout backstops it.
        if (fingerprint !== null) rememberDismissedFingerprint(fingerprint);
        return;
      }
      clearDismissedFingerprint();
      removeLines(conflicts)
        .then(function () { location.reload(); })
        .catch(function (err) {
          console.error('StockedDelivery: revalidate() failed to remove conflicting lines', err);
          // Same partial-success handling as reconcileForDate()'s callers: a
          // rejection can still mean some lines already came out server-side.
          if (err && err.removedCount > 0) location.reload();
          else dismissReconcile(); // nothing reloads -- don't strand the held dialog
        });
    });
  }

  /* ------------------------------------------------------- frozen-cart recovery */

  // The delivery-date validation function is evaluated against the cart a write
  // ARRIVES at, not the cart the write would produce. So the moment a cart stops
  // satisfying it, EVERY storefront mutation is refused: removing the offending
  // line, clearing the cart, even writing a fresh valid date (all three verified
  // against production, 2026-08-28). Nothing the buyer does from inside that cart
  // can fix it, and the state arrives unprompted -- a delivery date goes past its
  // cutoff because four days passed, and a product's availability weeks roll
  // behind the horizon while the cart sits.
  //
  // So recovery abandons the cart token and rebuilds. cartFrozenReason() predicts
  // the function's verdict locally (no write, so no 422 needed to find out) and
  // recoverCart() performs the rebuild. WHAT to rebuild is decided by the pure
  // planner in assets/cart-recovery.js.

  // The CART's own delivery_date -- the attribute the validation function reads --
  // and deliberately not localStorage. The frozen state belongs to the cart, so a
  // customer who built it on their phone and returned on a laptop must still see
  // it. That is the gap in revalidate(), which reads getStored() and therefore
  // never fires for them.
  function cartAttrDate() {
    var el = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
    return (el && el.dataset && el.dataset.selectedDate) || '';
  }

  // Whether the validation function will refuse every write to this cart,
  // mirrored from the same inputs it uses: knownDates() already encodes weekday,
  // blackout, cutoff, lookahead and open special dates, and the pure
  // frozenReason() (assets/cart-reconcile.js) checks per-line availability against
  // the date's slot. Returns 'missing' | 'stale' | 'unavailable', or null when the
  // cart is writable. Fails OPEN (null) if either shared module is missing --
  // wrongly claiming a healthy cart is frozen would offer the customer a
  // destructive rebuild they don't need.
  function cartFrozenReason() {
    var reconcile = window.StockedCartReconcile;
    if (!window.StockedDeliveryFormat || !reconcile || typeof reconcile.frozenReason !== 'function') {
      return null;
    }
    return reconcile.frozenReason(
      cartLinesRaw(),
      cartAttrDate(),
      knownDates().map(function (e) { return e.iso; }),
      config()
    );
  }

  function cartJsUrl() {
    return ((window.routes && window.routes.cart_url) || '/cart') + '.js';
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  function readCartCookie() {
    var m = /(?:^|;\s*)cart=([^;]*)/.exec(document.cookie);
    return m ? m[1] : '';
  }

  function writeCartCookie(value) {
    document.cookie = 'cart=' + value + '; path=/';
  }

  // Attributes owned by the delivery date, rewritten from the new selection rather
  // than carried across. Everything else on the cart (gift note, initial zip, and
  // anything an app added) is the customer's and survives verbatim.
  var DELIVERY_ATTR_KEYS = [
    'delivery_date', 'delivery_date_label', 'delivery_date_timestamp',
    'delivery_date_cutoff', 'delivery_week_code', 'delivery_window_label',
    'Order Due Date',
  ];

  function preservedAttributes(attrs) {
    var out = {};
    Object.keys(attrs || {}).forEach(function (key) {
      if (DELIVERY_ATTR_KEYS.indexOf(key) === -1) out[key] = attrs[key];
    });
    return out;
  }

  // /cart/add.js line payloads from /cart.js items. Quantity, line item properties
  // and any selling plan are carried over, so a rebuilt line is indistinguishable
  // from the original -- the theme's package/bundle lines identify themselves
  // through properties (_bundleVariants, see snippets/cart-drawer.liquid), and a
  // rebuild that dropped them would silently produce malformed bundles.
  function addPayload(items) {
    return items.map(function (item) {
      var line = { id: item.id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
      var allocation = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      if (allocation && allocation.id) line.selling_plan = allocation.id;
      return line;
    });
  }

  // Rebuilds the cart on a fresh token, keeping every line that can still be
  // delivered and dropping only the ones no offerable date covers. Resolves with
  // the plan (targetISO, keep, drop) so the caller can tell the customer exactly
  // what changed; rejects if a fresh cart could not be established.
  //
  // Non-destructive on every failure path: the old cart is never mutated -- lines
  // are only ever ADDED to the new one -- and its token is handed back if any step
  // fails, which leaves the customer exactly where they started rather than with
  // an orphaned cart plus a half-built one.
  async function recoverCart() {
    var recovery = window.StockedCartRecovery;
    if (!recovery) throw new Error('Cart recovery is unavailable');

    var swap = await swapToFreshCart();
    var original = swap.original;
    var plan;

    try {
      plan = recovery.planRecovery({
        items: original.items,
        availability: cartLinesRaw(),
        selectable: knownDates(),
        preferredISO: cartAttrDate(),
        config: config(),
      });

      // Date first, lines second. A cart that already has lines and no date is
      // MSG_MISSING, and that refusal would then apply to every later write --
      // the same ordering product-form.js relies on when it syncs the attribute
      // before an add.
      var sel = plan.targetISO ? select(plan.targetISO) : null;
      if (!sel) clearStored();
      var attributes = preservedAttributes(original.attributes);
      if (sel) Object.assign(attributes, toAttributes(sel));
      if (Object.keys(attributes).length) await ajax(cartUpdateUrl(), { attributes: attributes });

      if (plan.keep.length) await ajax(cartAddUrl(), { items: addPayload(plan.keep) });
    } catch (err) {
      swap.restore();
      throw err;
    }

    return plan;
  }

  // Abandons the current cart token and confirms the store handed out a fresh,
  // empty cart in its place. Resolves with the original /cart.js payload and a
  // restore() that hands the old token back, for callers whose later steps fail.
  //
  // Confirming the token actually changed matters: if the cookie could not be
  // dropped, every write after this would land on the SAME frozen cart and be
  // refused -- rejecting here says so honestly instead of replaying the original
  // failure. The old token is already restored when this rejects.
  async function swapToFreshCart() {
    var original = await fetch(cartJsUrl(), { cache: 'no-store' }).then(function (r) { return r.json(); });
    var previousCookie = readCartCookie();
    function restore() {
      if (previousCookie) writeCartCookie(previousCookie);
    }
    document.cookie = 'cart=; Max-Age=0; path=/';

    var fresh = await fetch(cartJsUrl(), { cache: 'no-store' }).then(function (r) { return r.json(); });
    if (fresh.token === original.token || fresh.item_count > 0) {
      restore();
      throw new Error('Could not start a fresh cart');
    }
    return { original: original, fresh: fresh, restore: restore };
  }

  /* ---------------------------------------------------- stale-date auto-clear */

  // A cart whose delivery date has closed is EMPTIED rather than rebuilt: the
  // date is what the customer shopped against (availability is per week), so
  // carrying the lines onto a date they never chose is a worse surprise than
  // starting over. The customer is told why in the gate, which asks for a new
  // date. Only the 'stale' frozen reason takes this path -- 'unavailable' and
  // 'missing' still have a usable date and keep the recovery dialog.
  //
  // Done in place (re-rendered sections, no reload): this fires on page load and
  // on drawer open, and a reload there reads as the page loading twice.

  // Set for the duration of an in-flight clear. reconcileForDate() -- the first
  // step of every date commit -- waits on it, so a date picked in the gate while
  // the swap is still running lands on the NEW cart, never the frozen one.
  var pendingClear = null;

  // Carries the "we emptied your cart" notice to the next gate when the clear
  // happens on a page that has no gate (content pages, /cart). Session-scoped:
  // stale news isn't worth showing in a later visit.
  var CART_CLEARED_NOTICE_KEY = 'stockedCartClearedNotice';

  function rememberClearedNotice() {
    try {
      sessionStorage.setItem(CART_CLEARED_NOTICE_KEY, '1');
    } catch (e) {
      /* ignore -- worst case the gate opens without the explanation */
    }
  }

  function takeClearedNotice() {
    try {
      if (!sessionStorage.getItem(CART_CLEARED_NOTICE_KEY)) return '';
      sessionStorage.removeItem(CART_CLEARED_NOTICE_KEY);
    } catch (e) {
      return '';
    }
    return window.stockedGateClearedMessage || window.stockedGateStaleMessage || '';
  }

  function sectionInnerHTML(html, selector) {
    var node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!node) throw new Error('Section markup missing ' + selector);
    return node.innerHTML;
  }

  // Re-renders every server-rendered view of the cart via the Section Rendering
  // API, so a cart swapped out underneath the page is reflected without a
  // reload: the drawer and header bubble everywhere, plus the /cart page's own
  // sections when present. The inline <cart-delivery-date> lives inside these
  // sections, so its data-cart-availability / data-selected-date (read by
  // cartLinesRaw() and cartAttrDate()) are refreshed along with them. Rejects if
  // any section can't be fetched -- the caller reloads instead, since a
  // half-updated page is worse than a slower one.
  async function refreshCartSurfaces(cart) {
    var targets = [];
    var drawer = document.querySelector('cart-drawer');
    var drawerRoot = drawer && drawer.querySelector('#CartDrawer');
    if (drawerRoot) targets.push({ section: 'cart-drawer', el: drawerRoot, selector: '#CartDrawer' });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      targets.push({
        section: 'cart-icon-bubble',
        el: bubble.querySelector('.shopify-section') || bubble,
        selector: '.shopify-section',
      });
    }
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var node = document.getElementById(id);
      var wrapper = node && node.dataset.id && document.getElementById('shopify-section-' + node.dataset.id);
      if (wrapper) targets.push({ section: node.dataset.id, el: wrapper, selector: '.shopify-section' });
    });
    if (!targets.length) return;

    var url = ((window.routes && window.routes.cart_url) || '/cart')
      + '?sections=' + targets.map(function (t) { return t.section; }).join(',');
    var sections = await fetch(url, { cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error('Section render failed (' + r.status + ')');
      return r.json();
    });

    // Parse everything before touching the DOM, so a missing section leaves the
    // page untouched for the reload fallback rather than half-swapped.
    var html = targets.map(function (t) {
      if (!sections[t.section]) throw new Error('Section ' + t.section + ' not rendered');
      return sectionInnerHTML(sections[t.section], t.selector);
    });
    targets.forEach(function (t, i) { t.el.innerHTML = html[i]; });

    if (drawer) {
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      // The overlay lives inside #CartDrawer and was just replaced; its close
      // listener went with it (cart-drawer.js renderContents() rebinds the same way).
      var overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
    }

    // Lets the rest of the theme (free-shipping bar, weekly-menu cleanup) see the
    // empty cart. Source 'cart-items' is the theme's own "sections already
    // painted" signal -- the <cart-items> subscribers skip it rather than
    // refetching what was just rendered.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }
  }

  // Empties a cart whose delivery date has closed, on a fresh token (the frozen
  // cart refuses /cart/clear.js like every other write), keeping only the
  // non-delivery attributes (zip, gift note, app data). Opens the gate with the
  // explanation right away on shop surfaces -- synchronously, so cart-drawer.js's
  // hasOpenDialog() check sees it and doesn't open the drawer over it -- and
  // leaves the notice for the next gate elsewhere.
  //
  // If a fresh cart can't be started, the cart is left exactly as it was and the
  // recovery dialog is offered instead, so the customer still has a way out.
  function clearStaleCart() {
    if (pendingClear) return;
    clearStored();
    renderBar();

    var gateOpened = false;
    if (document.body.hasAttribute('data-shop-surface') && selectableDates().length) {
      openGate(window.stockedGateClearedMessage || window.stockedGateStaleMessage || '');
      gateOpened = true;
    } else {
      rememberClearedNotice();
    }

    pendingClear = swapToFreshCart()
      .then(function (swap) {
        var attributes = preservedAttributes(swap.original.attributes);
        // Best effort: the cart is already empty and writable, which is the point.
        // Losing a gift note is not worth handing back the frozen cart over.
        var carried = Object.keys(attributes).length
          ? ajax(cartUpdateUrl(), { attributes: attributes }).catch(function (err) {
              console.error('StockedDelivery: could not carry cart attributes onto the cleared cart', err);
            })
          : null;
        return Promise.resolve(carried).then(function () {
          return refreshCartSurfaces(swap.fresh).catch(function (err) {
            console.error('StockedDelivery: could not re-render the cleared cart; reloading', err);
            location.reload();
            throw err;
          });
        });
      }, function (err) {
        console.error('StockedDelivery: could not clear a cart with a closed delivery date', err);
        try {
          sessionStorage.removeItem(CART_CLEARED_NOTICE_KEY);
        } catch (e) {
          /* ignore */
        }
        if (gateOpened && api._modal) {
          api._modal.blocking = false;
          var gate = document.getElementById('StockedDeliveryGate');
          if (gate) gate.dispatchEvent(new CustomEvent('stocked:gate-resolved', { detail: { selection: null } }));
        }
        offerRecoveryDialog();
        throw err;
      })
      .finally(function () {
        pendingClear = null;
      });
    // Rejections are fully handled above; this keeps them from surfacing as
    // unhandled while still letting reconcileForDate() see the failure.
    pendingClear.catch(function () {});
  }

  function windowLabelForISO(iso) {
    var fmt = window.StockedDeliveryFormat;
    var p = String(iso).split('-');
    return fmt.deliveryWindowLabel(+p[0], +p[1], +p[2], deliveryWeekdays());
  }

  // Names every line that can no longer be delivered BEFORE anything happens -- a
  // cart quietly losing items is never an acceptable outcome here, same rule the
  // reconcile dialog follows. Says nothing about the rebuild itself: to the
  // customer this is "these items are gone", not a cart migration.
  //
  // The delivery date is mentioned ONLY when it has to move. planRecovery keeps
  // the customer's own date whenever it still works, so in the common case there
  // is no date change to report and announcing one would be noise at best and
  // alarming at worst.
  function promptRecovery(plan) {
    var el = document.getElementById('StockedRecoveryDialog');
    if (!el) return Promise.resolve('cancel');
    fillItemList(el.querySelector('[data-recovery-items]'), plan.drop);

    var dateEl = el.querySelector('[data-recovery-date]');
    if (dateEl) {
      var moved = plan.targetISO && plan.targetISO !== cartAttrDate();
      if (plan.targetISO && !moved) {
        dateEl.hidden = true;
      } else {
        dateEl.textContent = plan.targetISO
          ? (dateEl.dataset.dateTemplate || '').replace('{{ window }}', windowLabelForISO(plan.targetISO))
          : dateEl.dataset.noDate || '';
        dateEl.hidden = false;
      }
    }
    return runDialog(el, { hold: true });
  }

  // Handles a frozen cart: a closed delivery date empties it (clearStaleCart()),
  // anything else offers the rebuild. Returns true when either started, so callers
  // can stand down -- nothing else they might do can write to this cart anyway.
  function offerRecovery() {
    var reason = cartFrozenReason();
    if (!reason) return false;
    if (reason === 'stale') {
      clearStaleCart();
      return true;
    }
    return offerRecoveryDialog();
  }

  function offerRecoveryDialog() {
    var recovery = window.StockedCartRecovery;
    if (!recovery) return false;

    // Planned from the rendered per-line availability rather than /cart.js so the
    // dialog opens SYNCHRONOUSLY: cart-drawer.js decides whether to open the
    // drawer by calling hasOpenDialog() the moment revalidate() returns, and the
    // drawer is z-indexed above this dialog -- an async open would surface it
    // underneath, with its buttons unclickable. Exempt lines are never dropped
    // and never constrain the date, so planning without them yields the same drop
    // list and the same target date; recoverCart() re-plans from /cart.js for the
    // rebuild itself, where quantities and properties do matter.
    var lines = cartLinesRaw();
    var plan = recovery.planRecovery({
      items: lines,
      availability: lines,
      selectable: knownDates(),
      preferredISO: cartAttrDate(),
      config: config(),
    });

    var el = document.getElementById('StockedRecoveryDialog');
    promptRecovery(plan)
      .then(function (choice) {
        if (choice !== 'confirm') return null;
        // The dialog stays up, busy, through the rebuild AND the load that
        // follows -- location.reload() is deliberately not awaited away. The
        // rebuild ends in a navigation, and dropping the dialog first left the
        // customer looking at an unchanged page for the length of two cart writes
        // plus a page load with nothing to indicate anything was happening.
        return recoverCart().then(function () { location.reload(); });
      })
      .catch(function (err) {
        console.error('StockedDelivery: cart recovery failed', err);
        if (el) {
          dismissDialog(el);
          if (el.dataset.error) window.alert(el.dataset.error);
        }
      });
    return true;
  }

  const api = {
    selectableDates,
    selectableWeeks,
    // Special (catering) dates: see openSpecialDates() for why they sit outside the
    // regular horizon. Later surfaces (catering banner, PDP, menu notice) build on these.
    openSpecialDates,
    knownDates,
    selectableSlots,
    slotOf,
    menuSlot,
    // The date the delivery bar names (the first regular date on a catering cart):
    // product-form.js commits it for a menu add over a catering cart.
    barDate,
    specialDateLabel,
    slotLabel,
    findDate,
    getSelection,
    select,
    needsSelection,
    requireSelection,
    selectionPromptsModal,
    promptReconcile,
    // product-form.js completes a catering add after the reconcile dialog instead of
    // reloading, so it takes the held dialog down itself.
    dismissReconcile,
    cartLinesRaw,
    removeLines,
    windowLabelForWeek,
    // Task 14: the PDP switch-week affordance is a THIRD commit point (besides
    // <cart-delivery-date>#selectDate and the shared [data-gate-continue] handler) that
    // needs the same "reconcile BEFORE mutating" guarantee -- exposed here rather than
    // duplicated so all three commit paths share one fail-open implementation.
    reconcileForDate,
    // Same reasoning as reconcileForDate above -- the PDP switch button hits the same
    // "removal succeeded but the picked date stopped being selectable" edge case, so it
    // reuses this instead of re-copying the alert copy + reload.
    warnDateNotApplied,
    hasOpenDialog,
    // Frozen-cart recovery. Exposed so the cart/drawer surfaces can offer it, and
    // so it can be driven from the console while the escape path is being verified
    // against the live validation function.
    cartFrozenReason,
    recoverCart,
    offerRecovery,
    openGate,
    enforceGate,
    revalidate,
    renderBar,
    toAttributes,
    setCartAttributes,
    clearStored,
    formatLong,
    deliveryWeekdays,
    cutoffConfig,
    // The parsed delivery_config and store-time today, for the special-date banner
    // (assets/special-date-banner.js), which decides its state from both.
    config,
    todayISO,
    WEEKDAYS,
    MONTHS,
    _modal: null,
  };
  window.StockedDelivery = api;

  /* ----------------------------------------------------- <cart-delivery-date> */

  class CartDeliveryDate extends HTMLElement {
    connectedCallback() {
      // The modal instance's markup (snippets/delivery-date-modal.liquid) never
      // carries data-cart-availability -- cartAllowedWeeks() reads it as unconstrained
      // (null) here, same as the old this.maxDate reading an absent data-max-date; it
      // gets a real value later via setAllowedWeeks() from open()/openGate().
      this.setAllowedWeeks(cartAllowedWeeks(this));
      this.mode = this.dataset.mode || 'inline';
      this.calendarEl = this.querySelector('[data-delivery-calendar]');
      this.summaryEl = this.querySelector('[data-delivery-summary]');
      if (!this.calendarEl || !this.summaryEl) return;

      this.refreshDates();

      // Reconcile server attribute (cart) with localStorage.
      const serverDate = this.dataset.selectedDate || null;
      if (serverDate && this.dates.has(serverDate)) {
        this.selectedDate = serverDate;
        if (this.mode !== 'modal') {
          const stored = getStored();
          if (!stored || stored.iso !== serverDate) select(serverDate);
        }
      } else {
        const stored = getSelection();
        if (stored) {
          this.selectedDate = stored.iso;
          // Inline (cart) surfaces push the stored date to the cart attribute if it
          // wasn't written yet (e.g. immediately after add).
          if (this.mode !== 'modal') {
            setCartAttributes(stored);
          }
        } else {
          this.selectedDate = null;
        }
      }

      this.viewMonth = this.initialMonth();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      if (this.mode === 'modal') {
        api._modal = this;
        this.hidden = true;
        // Set (and cleared) by openGate() / open() / previewDate(): whether the
        // dialog is the blocking gate (no dismiss affordance) and which day, if
        // any, is picked-but-not-yet-confirmed via the [data-gate-continue] button.
        this.blocking = false;
        this.pendingDate = null;
      } else {
        this.expanded = !this.selectedDate || !this.isAllowed(this.selectedDate);
        this.render();
        this.syncCheckoutButton();
      }
    }

    isAllowed(iso) {
      return isWeekAllowed(iso, this.allowedWeeks);
    }

    // Sets allowedWeeks AND reflects it onto data-allowed-weeks (see
    // reflectAllowedWeeks() above) in one place, so the two can never drift.
    setAllowedWeeks(weeks) {
      this.allowedWeeks = weeks;
      reflectAllowedWeeks(this, weeks);
    }

    refreshDates() {
      // Regular dates stay in the map even outside allowedWeeks (selectableList below
      // is what filters them), so a cart date its lines no longer allow is still
      // recognised and shown as needing a change. Special dates enter only when the
      // cart's slots explicitly offer them -- never on an empty/unconstrained cart.
      const specials = window.StockedAllowedWeeks.offeredDates(openSpecialDates(), this.allowedWeeks, config());
      this.dates = new Map(selectableDates().concat(specials).map((e) => [e.iso, e]));
      this.selectableList = Array.from(this.dates.keys())
        .filter((iso) => this.isAllowed(iso))
        .sort();
    }

    /* ------------------------------------------------------------ modal API */

    // Opens the shared #StockedDeliveryGate dialog for a CONSTRAINED RE-PICK (an item
    // in the cart -- or being added -- caps the deliverable weeks) rather than the
    // blocking gate -- dismissible via overlay click, Escape, or the visible Cancel
    // button (wired at the bottom of this file), unlike openGate(). Focus is still
    // trapped inside the dialog (matches every other modal in this theme); close()
    // always releases it. Resolves with the new selection, or the existing stored one
    // if dismissed without picking.
    open(opts) {
      opts = opts || {};
      this.setAllowedWeeks(opts.allowedWeeks || null);
      this.blocking = false;
      this.refreshDates();
      // alreadySelected mirrors the SAME guard used to enable Continue below
      // (updateGateSelection) -- valid only when there's a prior PERSISTED selection
      // (getSelection(), the localStorage/cart-attribute source of truth -- NOT
      // this.selectedDate), it's within the (possibly absent) allowedWeeks constraint,
      // and it's still a real open date after refreshDates(). Reading getSelection()
      // rather than this.selectedDate matters because previewDate() (below) mutates
      // this.selectedDate on every calendar-day click, purely to drive the .is-selected
      // highlight, before anything is committed -- if the customer previews a day B and
      // then dismisses, this.selectedDate is left pointing at the abandoned B even
      // though A is still the actual delivery date. Sourcing from the persisted value
      // instead makes this correct independent of that cleanup discipline (close()
      // below also resets this.selectedDate on dismiss, so the highlight matches too,
      // but pendingDate no longer depends on it). Seed pendingDate from it (instead of
      // unconditionally clearing it) so Continue is enabled BECAUSE there is a pending
      // date to commit, not merely styled as if there were: previously Continue could
      // show enabled with no day re-clicked, and the shared [data-gate-continue] handler
      // gates on pendingDate, not selectedDate, so pressing it silently did nothing.
      // When there's no valid persisted selection (the requireSelection() case, or one
      // outside allowedWeeks) alreadySelected is null, pendingDate stays null, and a
      // deliberate pick is still required -- unchanged.
      const stored = getSelection();
      const alreadySelected =
        stored && this.isAllowed(stored.iso) ? this.dates.get(stored.iso) : null;
      this.pendingDate = alreadySelected ? stored.iso : null;
      this.expanded = true;
      this.hidden = false;
      const gate = document.getElementById('StockedDeliveryGate');
      const dialog = gate ? gate.querySelector('.delivery-gate__dialog') : null;
      if (gate) {
        gate.hidden = false;
        updateGateChrome(gate, this.allowedWeeks ? allowedWeeksReasonText() : '', false);
        updateGateSelection(gate, alreadySelected);
      }
      document.body.classList.add('overflow-hidden');
      this.render();
      // Add the active class on the next frame so the dialog fades/slides in (transition).
      requestAnimationFrame(() => {
        if (gate) gate.classList.add('is-active');
      });
      const focusTarget =
        this.calendarEl.querySelector('[data-delivery-day][tabindex="0"]') ||
        this.calendarEl.querySelector('[data-delivery-day]');
      if (dialog && typeof trapFocus === 'function') {
        trapFocus(dialog, focusTarget || dialog);
      } else if (focusTarget) {
        focusTarget.focus();
      }
      return new Promise((resolve) => {
        this._resolveOpen = resolve;
      });
    }

    // The sole exit point for the constrained re-pick flow (CONTINUE, overlay-click,
    // Escape, and the Cancel button all route here) -- so releasing the focus trap
    // here, unconditionally, covers every dismissal path in one place.
    close(result) {
      const gate = document.getElementById('StockedDeliveryGate');
      if (gate) gate.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.hidden = true;
      // Never hide the gate wrapper out from under the blocking flow -- openGate()'s
      // own stocked:gate-resolved listener owns that transition.
      if (gate && !this.blocking) gate.hidden = true;
      const resolved = result !== undefined ? result : getSelection();
      // previewDate() may have left this.selectedDate pointing at a clicked-then-
      // rejected day (see open() above) -- reset it to the actual resolved/persisted
      // value on EVERY close (commit or dismiss) so the highlight is correct the next
      // time this dialog opens, regardless of which path got here.
      this.selectedDate = resolved ? resolved.iso : null;
      this.dataset.selectedDate = resolved ? resolved.iso : '';
      this.dataset.selectedLabel = resolved ? resolved.label : '';
      if (this._resolveOpen) {
        this._resolveOpen(resolved);
        this._resolveOpen = null;
      }
      this.setAllowedWeeks(null);
      this.pendingDate = null;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    initialMonth() {
      const anchor =
        (this.selectedDate && this.dates.get(this.selectedDate)?.day) ||
        (this.selectableList.length && this.dates.get(this.selectableList[0]).day) ||
        new Date();
      return { year: anchor.getFullYear(), month: anchor.getMonth() };
    }

    monthKey(year, month) {
      return year * 12 + month;
    }

    monthBounds() {
      if (!this.selectableList.length) {
        const now = new Date();
        const key = this.monthKey(now.getFullYear(), now.getMonth());
        return { min: key, max: key };
      }
      const first = this.dates.get(this.selectableList[0]).day;
      const last = this.dates.get(this.selectableList[this.selectableList.length - 1]).day;
      return {
        min: this.monthKey(first.getFullYear(), first.getMonth()),
        max: this.monthKey(last.getFullYear(), last.getMonth()),
      };
    }

    /* ---------------------------------------------------------------- render */

    // The cart's lines leave exactly one deliverable date and it's the selected one
    // (a catering cart: its special date). Nothing to change it to, so the inline
    // calendar shows the date without a Change control; switching to a regular date
    // goes through the menu's remove-items dialog. Never applies to the modal.
    hasOnlyDate() {
      return this.dataset.mode !== 'modal' &&
        !!this.selectedDate &&
        !!this.selectableList && this.selectableList.length === 1 &&
        this.selectableList[0] === this.selectedDate;
    }

    render() {
      if (this.hasOnlyDate()) this.expanded = false;
      this.renderSummary();
      if (this.expanded) {
        this.calendarEl.hidden = false;
        this.renderCalendar();
      } else {
        this.calendarEl.hidden = true;
        this.calendarEl.innerHTML = '';
      }
    }

    renderSummary() {
      if (this.selectedDate && this.isAllowed(this.selectedDate)) {
        const label = this.dates.get(this.selectedDate)
          ? formatShort(this.dates.get(this.selectedDate).day)
          : this.dataset.selectedLabel || this.selectedDate;
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label">Delivering on <strong>${label}</strong></span>` +
          (this.hasOnlyDate()
            ? ''
            : `<button type="button" class="cart-delivery-date__change link" data-delivery-action="toggle">` +
              `${this.expanded ? 'Done' : 'Change'}</button>`);
      } else if (this.allowedWeeks) {
        // A cart line (or the product mid-add) caps which weeks work, and no single
        // ceiling date can name it -- the allowed weeks can be non-contiguous. Explain
        // why a date is needed (or why the previously chosen one is no longer valid)
        // instead of a bare prompt, so the blocked checkout button has a visible
        // reason. WHICH item(s) conflict is Task 13's job (promptReconcile).
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label cart-delivery-date__label--prompt cart-delivery-date__label--warning">` +
          `${allowedWeeksReasonText()}</span>`;
      } else {
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label cart-delivery-date__label--prompt">Choose a delivery date</span>`;
      }
    }

    renderCalendar() {
      const { year, month } = this.viewMonth;
      const bounds = this.monthBounds();
      const currentKey = this.monthKey(year, month);
      const prevDisabled = currentKey <= bounds.min;
      const nextDisabled = currentKey >= bounds.max;

      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Position day 1 with grid-column-start (instead of leading empty <div>s, which a
      // global `div:empty { display:none }` would collapse and shift the whole grid).
      let cells = '';
      for (let dayNum = 1; dayNum <= daysInMonth; dayNum += 1) {
        const iso = toISO(year, month, dayNum);
        const entry = this.dates.get(iso);
        const isSelected = iso === this.selectedDate;
        const colStart = dayNum === 1 ? ` style="grid-column-start: ${firstWeekday + 1};"` : '';

        if (entry && this.isAllowed(iso)) {
          const ariaLabel = formatLong(entry.day);
          const tabindex =
            isSelected || (!this.selectedDate && iso === this.selectableList[0]) ? '0' : '-1';
          cells +=
            `<button type="button" class="cart-delivery-date__cell cart-delivery-date__cell--available` +
            `${isSelected ? ' is-selected' : ''}" data-delivery-day="${iso}" aria-label="${ariaLabel}"` +
            `${isSelected ? ' aria-pressed="true"' : ''} tabindex="${tabindex}"${colStart}>${dayNum}</button>`;
        } else {
          cells +=
            `<div class="cart-delivery-date__cell cart-delivery-date__cell--disabled" aria-disabled="true"${colStart}>${dayNum}</div>`;
        }
      }

      const weekdayHeader = WEEKDAYS.map(
        (d) => `<div class="cart-delivery-date__weekday">${d}</div>`
      ).join('');

      this.calendarEl.innerHTML =
        `<div class="cart-delivery-date__nav">` +
        `<button type="button" class="cart-delivery-date__arrow" data-delivery-action="prev"` +
        `${prevDisabled ? ' disabled' : ''} aria-label="Previous month">&#8249;</button>` +
        `<span class="cart-delivery-date__month" aria-live="polite">${MONTHS[month]} ${year}</span>` +
        `<button type="button" class="cart-delivery-date__arrow" data-delivery-action="next"` +
        `${nextDisabled ? ' disabled' : ''} aria-label="Next month">&#8250;</button>` +
        `</div>` +
        `<div class="cart-delivery-date__grid" role="grid">` +
        `<div class="cart-delivery-date__weekdays" role="row">${weekdayHeader}</div>` +
        `<div class="cart-delivery-date__days">${cells}</div>` +
        `</div>` +
        (this.selectableList.length
          ? ''
          : `<p class="cart-delivery-date__empty">No delivery dates are currently available.</p>`);
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      // Dismissal (overlay click / Cancel button) lives outside this element's own
      // subtree now -- in the shared #StockedDeliveryGate footer/overlay -- and is
      // wired as document-level listeners at the bottom of this file, guarded by
      // `blocking` the same way. There is nothing to catch here.
      const action = event.target.closest('[data-delivery-action]');
      if (action) {
        const type = action.dataset.deliveryAction;
        if (type === 'toggle') {
          this.expanded = !this.expanded;
          this.render();
        } else if (type === 'prev' && !action.disabled) {
          this.shiftMonth(-1);
        } else if (type === 'next' && !action.disabled) {
          this.shiftMonth(1);
        }
        return;
      }

      const dayButton = event.target.closest('[data-delivery-day]');
      if (dayButton) this.selectDate(dayButton.dataset.deliveryDay);
    }

    onKeydown(event) {
      if (this.mode === 'modal' && !this.blocking && event.key === 'Escape') {
        this.close();
        return;
      }

      const dayButton = event.target.closest('[data-delivery-day]');
      if (!dayButton) return;
      const current = dayButton.dataset.deliveryDay;

      if (event.key === 'Home') {
        event.preventDefault();
        this.focusDate(this.selectableList[0]);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        this.focusDate(this.selectableList[this.selectableList.length - 1]);
        return;
      }

      const deltas = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(event.key in deltas)) return;

      event.preventDefault();
      const idx = this.selectableList.indexOf(current);
      const next = idx + deltas[event.key];
      if (next >= 0 && next < this.selectableList.length) this.focusDate(this.selectableList[next]);
    }

    shiftMonth(delta) {
      const { year, month } = this.viewMonth;
      const d = new Date(year, month + delta, 1);
      this.viewMonth = { year: d.getFullYear(), month: d.getMonth() };
      this.renderCalendar();
    }

    focusDate(iso) {
      if (!iso) return;
      const day = this.dates.get(iso).day;
      const key = this.monthKey(day.getFullYear(), day.getMonth());
      if (key !== this.monthKey(this.viewMonth.year, this.viewMonth.month)) {
        this.viewMonth = { year: day.getFullYear(), month: day.getMonth() };
        this.renderCalendar();
      }
      const btn = this.calendarEl.querySelector(`[data-delivery-day="${iso}"]`);
      if (btn) {
        this.calendarEl
          .querySelectorAll('[data-delivery-day]')
          .forEach((b) => b.setAttribute('tabindex', '-1'));
        btn.setAttribute('tabindex', '0');
        btn.focus();
      }
    }

    /* ------------------------------------------------------------ selection */

    selectDate(iso) {
      if (this.mode === 'modal') {
        // Neither the blocking gate nor the constrained re-pick commits on a bare day
        // click anymore -- both wait for [data-gate-continue] (wired at the bottom of
        // this file), which is what "CONTINUE disabled until a date is picked" means.
        this.previewDate(iso);
        return;
      }

      // Task 13: this is a COMMIT point (unlike previewDate above), so it must be
      // reconciled against the current cart BEFORE anything is mutated -- see
      // reconcileForDate(). Guarded against a double-click firing a second
      // reconcile pass while the first is still awaiting the dialog/network.
      if (this._reconciling) return;
      this._reconciling = true;
      reconcileForDate(iso).then((result) => {
        this._reconciling = false;
        if (!result.proceed) {
          // Either a clean cancel (cart & date genuinely untouched), OR removal
          // failed partway through -- some lines may already be gone server-side
          // even though we're NOT applying the new date. result.removed
          // distinguishes the two (see removeLines()'s partial-success tracking):
          // reload whenever it's true so the page never shows a cart that's out of
          // sync with what the server actually has.
          if (result.removed) location.reload();
          return;
        }
        const applied = this._commitDate(iso);
        if (!applied) {
          // The date itself couldn't be applied (see warnDateNotApplied()) -- if
          // removals already happened, that's a half-landed destructive action the
          // customer needs to know about; if nothing was removed either, there's
          // simply nothing to commit (matches the pre-Task-13 silent-no-op here).
          if (result.removed) warnDateNotApplied();
          return;
        }
        if (result.removed) reloadAfter(this._persistence);
      });
    }

    // The actual commit -- unchanged from the pre-Task-13 body of selectDate(),
    // just split out so reconcileForDate() can run first without touching any
    // state, and returning a boolean so the caller can tell whether it actually
    // landed. Gating the mutation itself (rather than mutating then undoing on
    // cancel) is what keeps every entry/exit path of this audited state machine
    // independent of stale prior state, with nothing to unwind on cancel.
    _commitDate(iso) {
      const sel = select(iso); // writes localStorage synchronously
      if (!sel) return false;

      // Optimistic UI: reflect the choice immediately. select() already persisted the
      // selection to localStorage, so the line stamp on add (which reads localStorage)
      // is correct even if the network writes below are still in flight.
      this.selectedDate = iso;
      this.dataset.selectedDate = iso;
      this.dataset.selectedLabel = sel.label;

      // Every commit site (this one, the shared [data-gate-continue] handler, and the
      // PDP switch-week button in availability-filter.js) dispatches ONLY this event.
      // The single bridge below -- registered once, near the other document-level
      // wiring in this file -- is what decides whether the WEEK actually changed
      // (comparing the global getSelection() against the previously-seen global week,
      // not any one component's own field) and re-fires stocked:week-changed exactly
      // once when it does. That keeps every commit path (and the non-modal
      // <cart-delivery-date> instance other paths don't own) in sync without each site
      // re-deriving "did the week move" itself.
      document.dispatchEvent(
        new CustomEvent('delivery-date:changed', { detail: { date: iso, label: sel.label } })
      );

      this.expanded = false;
      this.render();
      this.syncCheckoutButton();

      // Persist to the cart in the background (cart attributes + re-stamp line items).
      // Kept on the instance so selectDate() can wait for it before reloading -- see
      // reloadAfter(). Still not awaited here: the commit itself stays synchronous.
      this._persistence = this.persistSelection(sel);
      return true;
    }

    // Highlights a day as picked-but-not-yet-confirmed and updates the gate footer.
    // Nothing is persisted (localStorage/cart) until [data-gate-continue] is pressed.
    previewDate(iso) {
      const entry = this.dates.get(iso);
      if (!entry) return;
      this.pendingDate = iso;
      this.selectedDate = iso; // drives the .is-selected highlight in renderCalendar()
      this.renderCalendar();
      updateGateSelection(document.getElementById('StockedDeliveryGate'), entry);
    }

    async persistSelection(sel) {
      try {
        await setCartAttributes(sel);
      } catch (error) {
        console.error('cart-delivery-date: failed to persist selection', error);
      }
    }

    // Checkout has two independent gates: a chosen delivery date (this component) AND a
    // valid zip (cart-zip-validator). Each owns a data-attribute marker; the button is
    // enabled only when neither gate is blocking — so the two don't fight over `disabled`.
    syncCheckoutButton() {
      document
        .querySelectorAll('#CartDrawer-Checkout, #checkout, .cart__checkout-button')
        .forEach((btn) => {
          btn.toggleAttribute('data-needs-delivery-date', !this.selectedDate || !this.isAllowed(this.selectedDate));
          const blocked =
            btn.hasAttribute('data-needs-zip') || btn.hasAttribute('data-needs-delivery-date');
          btn.disabled = blocked;
          btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        });
    }
  }

  if (!customElements.get('cart-delivery-date')) {
    customElements.define('cart-delivery-date', CartDeliveryDate);
  }

  /* ------------------------------------------- delivery-date:changed bridge */

  // The SINGLE listener for delivery-date:changed. Three commit sites dispatch it --
  // this file's own _commitDate() and the shared [data-gate-continue] handler below,
  // plus the PDP switch-week button in availability-filter.js -- and none of them
  // dispatch stocked:week-changed directly anymore. This bridge has two jobs:
  //
  //   1. Decide whether the WEEK actually changed and re-fire stocked:week-changed
  //      exactly once when it did, so availability-filter.js's apply() (and
  //      renderBar()) run once per genuine change, not once per commit. Comparing
  //      against the GLOBAL selection (getSelection(), the localStorage/cart-attribute
  //      source of truth) rather than any one component's own field is the fix: a
  //      component's optimistic field goes stale the moment a SIBLING commit site
  //      changes the week without that component's knowledge, which could previously
  //      suppress a real change (prevWeek === newWeek by coincidence) or double-fire.
  //   2. Refresh the one non-modal <cart-delivery-date> on the page (the inline
  //      cart-page/drawer calendar) -- the gate/modal instance and the PDP button each
  //      already update their OWN state directly, but neither touches this sibling
  //      element, which is how it used to desync from the real selection.
  //
  // lastKnownWeek is seeded from whatever's already stored at script-load time (page
  // load, before any commit can happen), not left as an unset sentinel -- so the
  // first commit of the session is compared against the REAL prior week and doesn't
  // spuriously fire when it lands in the same week as the one already on screen.
  // Compared by SLOT, not week code: moving between a special date and a regular
  // date in the same calendar week changes what's orderable, so it must re-filter.
  var lastKnownWeek = (function () {
    var sel = getSelection();
    return sel && window.StockedDeliveryFormat ? slotOf(sel.iso) : null;
  }());

  document.addEventListener('delivery-date:changed', function () {
    var sel = getSelection();
    var newWeek = sel && window.StockedDeliveryFormat ? slotOf(sel.iso) : null;
    var weekChanged = newWeek !== lastKnownWeek;
    lastKnownWeek = newWeek;
    // Fires a DIFFERENT event (stocked:week-changed), never delivery-date:changed
    // itself -- this listener can't re-enter itself from its own dispatch.
    if (weekChanged) {
      document.dispatchEvent(new CustomEvent('stocked:week-changed', { detail: { selection: sel } }));
    }

    if (!sel) return;
    var inline = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
    if (inline && typeof inline.render === 'function') {
      inline.selectedDate = sel.iso;
      inline.dataset.selectedDate = sel.iso;
      inline.dataset.selectedLabel = sel.label;
      inline.render();
      if (typeof inline.syncCheckoutButton === 'function') inline.syncCheckoutButton();
    }
  });

  /* ------------------------------------------------------ persistent delivery bar */

  // #StockedDeliveryBar (snippets/delivery-bar.liquid) shows the chosen date + order-by
  // cutoff on shop surfaces once a date is picked -- it's the "you can change this any
  // time" affordance the gate's copy promises. Pure re-render, safe to call repeatedly
  // (idempotent on the current getSelection() / cutoffConfig() state): DOMContentLoaded,
  // every stocked:week-changed, and the CHANGE click handler below all call it, and Task
  // 12's revalidation pass will call it too so a cutoff that's since passed is never
  // left displayed as still open.
  // The date the delivery bar shows. A special (catering) selection has no menu of its
  // own, so the menu surfaces carrying the bar read as if no date were chosen yet: the
  // bar names the first regular date, matching the menu below it (menuSlot()). The
  // stored selection is untouched; Change and add-to-cart still go through the
  // remove-items dialog for the catering lines.
  function barDate() {
    var sel = getSelection();
    if (!sel || !window.StockedDeliveryFormat.isSpecialDate(sel.iso, config())) return sel;
    return selectableDates()[0] || null;
  }

  function renderBar() {
    renderSpecialCartNotices();
    var bar = document.getElementById('StockedDeliveryBar');
    if (!bar) return;
    var sel = barDate();
    if (!sel) {
      // No selection yet (or it went stale) -- the gate is the only affordance
      // pre-selection. Never show the bar with empty/stale content.
      delete bar.dataset.weekCode;
      bar.hidden = true;
      return;
    }
    var fmt = window.StockedDeliveryFormat;
    var p = sel.iso.split('-');
    var y = +p[0], m = +p[1], d = +p[2];
    bar.querySelector('[data-bar-date]').textContent = fmt.deliveryBarDateLabel(y, m, d);
    var c = fmt.deliveryCutoff(y, m, d, cutoffConfig());
    // Display time is hardcoded to 12pm -- the real cutoff (used for validation) is
    // 12:30, see deliveryCutoff()'s comment. Customers just see the friendlier round hour.
    bar.querySelector('[data-bar-cutoff]').textContent =
      'Order by ' + fmt.deliveryBarDateLabel(c.y, c.m, c.d) + ' · 12pm';
    bar.dataset.weekCode = fmt.weekCodeOf(sel.iso);
    bar.hidden = false;
  }

  // [data-special-cart-notice] (snippets/special-cart-notice.liquid, on the menu
  // surfaces): shown only while the cart is set for a special (catering) date, saying
  // why the menu shows other days. Filled here, alongside the bar, on every change.
  function renderSpecialCartNotices() {
    var sel = getSelection();
    var fmt = window.StockedDeliveryFormat;
    Array.prototype.forEach.call(document.querySelectorAll('[data-special-cart-notice]'), function (el) {
      var text = sel
        ? fmt.specialCartNotice(sel.iso, config(), { label: el.dataset.noticeLabel, noLabel: el.dataset.noticeNoLabel })
        : '';
      el.textContent = text;
      el.hidden = !text;
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-bar-change]')) return;
    var modal = api._modal;
    if (!modal) return;
    // modal.open() is the DISMISSIBLE re-pick flow (Cancel / Escape / overlay-click all
    // work, and it pre-highlights the customer's current selection with Continue already
    // enabled) -- NOT openGate(), which is deliberately inescapable (no Cancel, no Escape,
    // no overlay-click, clears the footer to "No date selected") and exists only for the
    // pre-shop blocking gate. A customer with a confirmed date clicking CHANGE just to
    // look must be able to back out without being forced into a fresh pick.
    // Also NOT requireSelection(): with no allowedWeeks constraint and an already-valid
    // selection it resolves immediately without opening anything, so CHANGE would
    // appear to do nothing.
    var before = getSelection();
    var beforeIso = before ? before.iso : null;
    // Dismissing open() resolves with the unchanged prior selection (see
    // CartDeliveryDate#close). The shared [data-gate-continue] handler below already
    // commits a real pick and dispatches stocked:week-changed (which renderBar() listens
    // for) before this promise resolves, so this re-render is only needed as a fallback --
    // guarded so an unchanged (dismissed) selection never re-dispatches or re-renders.
    modal.open().then(function (sel) {
      if (sel && sel.iso !== beforeIso) renderBar();
    });
  });

  document.addEventListener('DOMContentLoaded', renderBar);
  document.addEventListener('stocked:week-changed', renderBar);

  /* --------------------------------------------------- gate / modal wiring */

  // Commits whatever day is currently previewed (CartDeliveryDate#pendingDate) via
  // [data-gate-continue]. Handles both the blocking gate (resolves openGate()'s promise
  // through stocked:gate-resolved) and the constrained re-pick (resolves modal.open()
  // through modal.close()) -- see previewDate() above for why nothing is persisted
  // before this point.
  //
  // Task 13: this is the OTHER commit point (besides <cart-delivery-date>#selectDate
  // above), so it's reconciled the same way -- BEFORE select()/setCartAttributes ever
  // run. On cancel (or a failed removal), the handler returns having touched nothing:
  // pendingDate is still set, the gate/modal is still open exactly as it was, and
  // Continue is clickable again (see the _committing guard below).
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-gate-continue]');
    if (!btn || btn.disabled) return;
    var modal = api._modal;
    if (!modal || !modal.pendingDate || modal._committing) return;
    var iso = modal.pendingDate;
    modal._committing = true;

    reconcileForDate(iso).then(function (result) {
      modal._committing = false;
      if (!result.proceed) {
        // Either a clean cancel (gate/modal genuinely untouched), OR removal
        // failed partway through -- see the equivalent branch in selectDate()
        // above for why result.removed still needs checking, and reloading, here.
        if (result.removed) location.reload();
        return;
      }

      var sel = select(iso);
      if (!sel) {
        // Matches selectDate()'s degrade behaviour: the picked date stopped being
        // selectable in the time the dialog was open. If removals already
        // happened server-side, that's a half-landed destructive action the
        // customer approved -- tell them, don't fail silently.
        if (result.removed) warnDateNotApplied();
        return;
      }

      modal.pendingDate = null;
      modal.selectedDate = sel.iso;
      modal.dataset.selectedDate = sel.iso;
      modal.dataset.selectedLabel = sel.label;

      var persistence = setCartAttributes(sel);
      // The delivery-date:changed bridge (registered above, alongside the other
      // document-level wiring in this file) picks this up and re-fires
      // stocked:week-changed only if the week actually moved -- see _commitDate()
      // above for why every commit site dispatches only this one event now.
      document.dispatchEvent(new CustomEvent('delivery-date:changed', { detail: { date: sel.iso, label: sel.label } }));

      if (modal.blocking) {
        modal.blocking = false;
        var gate = document.getElementById('StockedDeliveryGate');
        if (gate) gate.dispatchEvent(new CustomEvent('stocked:gate-resolved', { detail: { selection: sel } }));
      } else {
        modal.close(sel);
      }

      if (result.removed) reloadAfter(persistence);
    });
  });

  // Overlay-click dismissal for the CONSTRAINED RE-PICK only -- a no-op while the
  // blocking gate is open (modal.blocking is true, see the "trap to avoid" note above).
  document.addEventListener('click', function (event) {
    if (!event.target.closest('.delivery-gate__overlay')) return;
    var modal = api._modal;
    if (!modal || modal.blocking) return;
    modal.close();
  });

  // Visible Cancel button, hidden by updateGateChrome() while blocking is true -- same
  // guard as the overlay-click handler above, kept for defense in depth.
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-gate-cancel]')) return;
    var modal = api._modal;
    if (!modal || modal.blocking) return;
    modal.close();
  });

  // Escape dismissal for the CONSTRAINED RE-PICK only. Handled at the document level
  // (rather than relying on the element's own onKeydown) because focus while this
  // dialog is open can land on [data-gate-continue], which lives outside
  // <cart-delivery-date>'s own subtree.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var modal = api._modal;
    if (!modal || modal.blocking) return;
    // Registered at module load, so it runs BEFORE promptReconcile()'s own (later,
    // dynamically-added) Escape handler on the same event -- stopPropagation() in
    // the later one can't undo an earlier one having already fired. Bail here
    // instead: while the reconcile dialog is stacked on top, IT owns Escape (and
    // dismissing it leaves this dialog exactly as it was, still open).
    var reconcileEl = document.getElementById('StockedReconcileDialog');
    if (reconcileEl && !reconcileEl.hidden) return;
    var gate = document.getElementById('StockedDeliveryGate');
    if (gate && !gate.hidden) modal.close();
  });

  // Blocking gate: there is no default delivery-date selection, so shop surfaces
  // (body[data-shop-surface], set in layout/theme.liquid) must not be usable until the
  // customer picks one. Guarded so a failure here can't leave the page permanently
  // unusable -- openGate() already no-ops if #StockedDeliveryGate is missing, and if
  // enforceGate() itself throws, the catch below force-hides the gate and clears the
  // scroll lock rather than leaving a dimmed, dead page behind.
  //
  // revalidate() runs FIRST, and gates whether enforceGate() runs at all: a stale
  // stored date means revalidate() has already opened the gate itself (with the stale
  // explanation) -- calling enforceGate() right after would open a SECOND time with no
  // reason text, clobbering the one revalidate() just set. revalidate() returns true
  // both when the selection is still valid AND when nothing was ever stored, so
  // enforceGate()'s own no-default-selection check still runs normally in both of
  // those cases.
  document.addEventListener('DOMContentLoaded', function () {
    try {
      if (revalidate()) enforceGate();
    } catch (err) {
      console.error('StockedDelivery: enforceGate() failed; catalog left unblocked', err);
      var el = document.getElementById('StockedDeliveryGate');
      if (el) {
        el.hidden = true;
        el.classList.remove('is-active');
      }
      document.body.classList.remove('delivery-gate-open');
    }
  });

  // Re-running revalidation on cart-drawer open (the blocking gate above only covers
  // PAGE LOAD, but the drawer can sit open -- or be reopened -- across a long dwell on
  // the same page with no intervening navigation) is wired directly in
  // assets/cart-drawer.js's own open(), which calls window.StockedDelivery.revalidate()
  // -- see that file for the same dueling-modal guard against opening the drawer
  // underneath a gate revalidate() just opened.
})();
